import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

export default function Home() {
  const API_BASE = import.meta.env.VITE_API_BASE_URL;
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/products`)
      .then((res) => setProducts(res.data.slice(0, 3))) // show 3 featured
      .catch(console.error);
  }, [API_BASE]);

  return (
    <div>
      {/* HERO */}
      <section className="bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              Elevate Your Salon & Home Wellness with{" "}
              <span className="text-rose-300">RelaxB</span>
            </h1>
            <p className="text-gray-300 mb-6 text-sm md:text-base">
              Premium massage chairs, shampoo massage beds, and pedicure chairs
              designed for comfort, durability, and a luxurious client
              experience.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/products"
                className="bg-white text-black px-5 py-2 rounded-full text-sm font-semibold hover:bg-gray-200"
              >
                Shop All Products
              </Link>
              <a
                href="#categories"
                className="border border-gray-500 px-5 py-2 rounded-full text-sm font-semibold hover:bg-gray-800"
              >
                View Categories
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl bg-gradient-to-tr from-rose-500/40 to-sky-500/40 blur-3xl absolute inset-0" />
            <div className="relative bg-gray-900/80 border border-gray-700 rounded-3xl p-6 shadow-2xl">
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                Featured Collection
              </div>
              <div className="font-semibold text-lg mb-2">
                Zero-Gravity Massage Chair
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Deep tissue massage, heat therapy, and zero-gravity recline —
                built to impress your clients or transform your living room.
              </p>
              <ul className="text-xs text-gray-300 space-y-1 mb-4">
                <li>• 3D roller system</li>
                <li>• Full-body air massage</li>
                <li>• Customizable programs</li>
              </ul>
              <div className="text-right text-xs text-gray-500">
                * Images for demo only
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold mb-4">Shop by Category</h2>
        <p className="text-gray-500 text-sm mb-6">
          Choose the perfect equipment for your salon, spa, or home.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          <CategoryCard
            name="Massage Chairs"
            description="Luxurious full-body massage chairs with heat and zero-gravity."
            link="/products?category=Massage%20Chair"
          />
          <CategoryCard
            name="Shampoo Massage Beds"
            description="Ergonomic shampoo beds with integrated massage for ultimate comfort."
            link="/products?category=Shampoo%20Bed"
          />
          <CategoryCard
            name="Pedicure Chairs"
            description="Premium pedicure thrones with whirlpool and massage functions."
            link="/products?category=Pedicure%20Chair"
          />
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Featured Products</h2>
          <Link
            to="/products"
            className="text-sm text-blue-600 hover:underline"
          >
            View all
          </Link>
        </div>

        {products.length === 0 ? (
          <p className="text-gray-500 text-sm">No products loaded yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {products.map((p) => (
              <Link
                key={p._id}
                to={`/products/${p._id}`}
                className="bg-white rounded-xl shadow hover:shadow-md transition overflow-hidden flex flex-col"
              >
                <div className="aspect-[4/3] bg-gray-100">
                  {p.images?.[0] ? (
                    <img
                      src={p.images[0]}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                      No image
                    </div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="text-xs uppercase text-gray-500 mb-1">
                    {p.category}
                  </div>
                  <div className="font-semibold text-sm mb-1 line-clamp-2">
                    {p.name}
                  </div>
                  <div className="text-sm font-bold mt-auto">
                    ${p.price?.toLocaleString()}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function CategoryCard({ name, description, link }) {
  return (
    <Link
      to={link}
      className="bg-white rounded-xl shadow hover:shadow-md transition p-5 flex flex-col"
    >
      <div className="font-semibold mb-1">{name}</div>
      <p className="text-sm text-gray-500 flex-1">{description}</p>
      <div className="text-xs text-blue-600 mt-3">Shop {name} →</div>
    </Link>
  );
}
